import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import Tab from "../../../components/tab/Tab";
import TitleBlock from "../../../components/block/TitleBlock";
import Button from "../../../components/button/Button";
import IconButton from "../../../components/button/IconButton";
import MediaPreview from "../../../components/media-preview/MediaPreview";
import StatusIndicator, { StatusType } from "../../../components/status-indicator/StatusIndicator";
import {
  useGetProfileContentQuery,
  useUpdateProfileContentMutation,
  type ProfileContentUpdate,
} from "../../../app/services/creatorProfile";
import useS3Upload from "../../../hooks/useS3Upload";
import { detectProfileMediaType } from "../../../utils/profileMedia";
import type {
  ProfileHeader,
  ProfileHeaderType,
  ProfileHeaderUpdate,
  ProfileMediaLayout,
  ProfileMediaType,
} from "../../../types/profile";

const EMPTY: ProfileHeader = {
  type_header: "full_screen",
  logo_url: null,
  key_visual_pc_url: null,
  key_visual_sp_url: null,
  banner_url: null,
  catchphrase_jp: [],
  catchphrase_en: [],
  display_artist_name: false,
  display_title: false,
  artist_name_jp: "",
  artist_name_en: "",
  header_title_jp: "",
  header_title_en: "",
};

const TAB_FULL = "フルスクリーン";
const TAB_BANNER = "バナー";

// 作品展示方法のタブ（表示ラベル ⇔ API 値）。
const LAYOUT_TAB_THUMBNAIL = "サムネイル";
const LAYOUT_TAB_CIRCLE = "サークル";
const LAYOUT_TAB_LIST = "リスト";

const LAYOUT_TO_TAB: Record<ProfileMediaLayout, string> = {
  thumbnail: LAYOUT_TAB_THUMBNAIL,
  circle: LAYOUT_TAB_CIRCLE,
  list: LAYOUT_TAB_LIST,
};

const TAB_TO_LAYOUT: Record<string, ProfileMediaLayout> = {
  [LAYOUT_TAB_THUMBNAIL]: "thumbnail",
  [LAYOUT_TAB_CIRCLE]: "circle",
  [LAYOUT_TAB_LIST]: "list",
};

const LAYOUT_DESCRIPTIONS: Record<ProfileMediaLayout, string> = {
  thumbnail: "すべての作品をサムネイルのグリッドで表示します。音源はクリックで画面下のプレイヤーが開きます。",
  circle:
    "音源作品を円形に配置して展示します。クリックで画面下のプレイヤーが開きます。音源以外の作品はサムネイルで併記されます。",
  list: "音源作品を波形付きのリストで表示します。音源以外の作品はサムネイルで併記されます。",
};

// キービジュアル / バナーは画像（JPG/PNG）と動画（MP4/MOV）の両方を受け付ける。
// 動画は Lambda で HLS、画像は WebP に最適化される。上限は動画を考慮して 200MB。
const MEDIA_ACCEPT = ["image/*", "video/*"];
const MAX_MEDIA_SIZE = 200 * 1024 * 1024;

/** 拡張子から画像/動画を推定（既存 URL のプレビュー種別判定用。MediaPreview に委譲できない blob 用の補助）。 */
const detectTypeFromUrl = (url: string | null): ProfileMediaType | undefined => {
  if (!url) return undefined;
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
  if (!ext) return undefined;
  if (["mp4", "mov", "webm", "m3u8"].includes(ext)) return "video";
  if (["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext)) return "image";
  return undefined;
};

interface KeyVisualUploaderProps {
  label: string;
  url: string | null;
  ratio: number;
  /** アップロード完了時に取得した file_key を親へ通知する（プレビューは内部 state で保持）。 */
  // eslint-disable-next-line no-unused-vars
  onUploaded: (fileKey: string) => void;
  onCleared: () => void;
}

/**
 * 1 枠分のキービジュアル / バナー編集カード。
 *
 * 現在のメディア（画像/動画）を `MediaPreview` で表示し、「差し替え」「削除」を行う。
 * アップロードは作品メディアと同じ `useS3Upload(purpose='work_media')` 経路で、
 * 返却された file_key を親に渡して `content` PUT で保存する。アップロード直後は
 * ローカルの objectURL を即時プレビューし、最適化版は次回読み込みで反映される。
 */
const KeyVisualUploader: React.FC<KeyVisualUploaderProps> = ({
  label,
  url,
  ratio,
  onUploaded,
  onCleared,
}) => {
  // override: undefined=propのurlを使用 / 設定済み=ユーザー操作後のローカル表示。
  const [override, setOverride] = useState<
    { url: string | null; type: ProfileMediaType | undefined } | undefined
  >(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { uploadFiles, isUploading } = useS3Upload({
    purpose: "work_media",
    skipRegister: true,
    restrictions: { accept: MEDIA_ACCEPT, maxSize: MAX_MEDIA_SIZE, maxFiles: 1 },
    onValidationError: (errors) => errors.forEach((e) => toast.error(e.message)),
  });

  const handlePick = useCallback(
    async (file: File) => {
      const type = detectProfileMediaType(file);
      const [result] = await uploadFiles([file]);
      if (result?.status === "completed" && result.fileKey) {
        setOverride({ url: URL.createObjectURL(file), type });
        onUploaded(result.fileKey);
      } else if (result?.status === "error") {
        toast.error("アップロードに失敗しました");
      }
    },
    [uploadFiles, onUploaded],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handlePick(file);
      e.target.value = "";
    },
    [handlePick],
  );

  const handleClear = useCallback(() => {
    setOverride({ url: null, type: undefined });
    onCleared();
  }, [onCleared]);

  const displayUrl = override !== undefined ? override.url : url;
  const displayType =
    override !== undefined ? override.type : detectTypeFromUrl(url);
  const isBusy = isUploading;

  return (
    <div className="u-col u-w100 u-gap4">
      <span className="bodytext-13">{label}</span>
      <div
        className="u-col-center"
        style={{
          width: "100%",
          maxWidth: 360,
          aspectRatio: String(ratio),
          background: "var(--soremo-background)",
          borderRadius: 4,
          border: "1px solid var(--soremo-border)",
          overflow: "hidden",
        }}
      >
        {displayUrl ? (
          <MediaPreview
            src={displayUrl}
            mediaType={displayType}
            autoPlay={displayType === "video"}
            sizeMode={{ mode: "fill", objectFit: "cover" }}
            disableHoverEffect
          />
        ) : (
          <span className="bodytext-11 u-text-light-gray">未設定</span>
        )}
      </div>
      <div className="u-row u-gap8" style={{ alignItems: "center" }}>
        <Button
          size="small"
          variant="outlined"
          theme="secondary"
          leftIcon="upload"
          onClick={() => inputRef.current?.click()}
          disabled={isBusy}
        >
          {displayUrl ? "差し替え" : "画像 / 動画を選択"}
        </Button>
        {displayUrl && (
          <IconButton
            icon="delete"
            size={18}
            onClick={handleClear}
            ariaLabel={`${label}を削除`}
            title="削除"
            disabled={isBusy}
          />
        )}
        {isUploading && (
          <span className="bodytext-11 u-text-light-gray">アップロード中...</span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={MEDIA_ACCEPT.join(",")}
        onChange={handleInputChange}
        style={{ display: "none" }}
      />
    </div>
  );
};

/**
 * マイサイトのデザイン（キービジュアル / 作品展示方法）編集。
 *
 * `/api/me/pages/profile/content` の header に接続し、配置方法（フルスクリーン/
 * バナー）を選択する。タブは現在の選択（type_header）に追従する。各枠のキー
 * ビジュアル / バナーは画像・動画をアップロードして差し替えできる（作品メディアと
 * 同じ S3 経路 + Lambda 最適化）。
 *
 * 作品の展示方法（`work_display_layout`: サムネイル / サークル / リスト）も
 * 同じ content PUT で即時保存する。
 *
 * キャッチフレーズ / アーティスト名 / タイトル / ロゴは廃止し、公開ページの
 * 見出しはプロフィールで設定した表示名・タイトルを共通利用する。
 */
const MySiteDesign: React.FC = () => {
  const { data: contentResp } = useGetProfileContentQuery();
  const [updateContent] = useUpdateProfileContentMutation();
  const [header, setHeader] = useState<ProfileHeader>(EMPTY);
  const [workLayout, setWorkLayout] = useState<ProfileMediaLayout>("thumbnail");
  const [status, setStatus] = useState<StatusType>("idle");
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!hydratedRef.current && contentResp?.data) {
      setHeader(contentResp.data.header || EMPTY);
      setWorkLayout(contentResp.data.work_display_layout || "thumbnail");
      hydratedRef.current = true;
    }
  }, [contentResp]);

  // content PUT の共通処理（保存中 → 完了/失敗のステータス表示を一元化）。
  const persistContent = useCallback(
    async (payload: ProfileContentUpdate) => {
      try {
        setStatus("saving");
        await updateContent(payload).unwrap();
        setStatus("saved");
        setTimeout(() => setStatus("idle"), 1500);
      } catch {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 2000);
      }
    },
    [updateContent],
  );

  const save = useCallback(
    async (patch: Partial<ProfileHeaderUpdate>, nextLocal?: ProfileHeader) => {
      if (nextLocal) setHeader(nextLocal);
      await persistContent({ header: { ...header, ...patch } });
    },
    [persistContent, header],
  );

  const handleTabChange = (tab: string) => {
    const type_header = (tab === TAB_BANNER ? "banner" : "full_screen") as ProfileHeaderType;
    const next = { ...header, type_header };
    save({ type_header }, next);
  };

  // 作品展示方法の切替（header とは独立した content トップレベルフィールド）。
  const handleLayoutTabChange = useCallback(
    (tab: string) => {
      const layout = TAB_TO_LAYOUT[tab] ?? "thumbnail";
      setWorkLayout(layout);
      void persistContent({ work_display_layout: layout });
    },
    [persistContent],
  );

  // タブは実際の選択中タイプ（type_header）に追従させる。
  const activeTab = header.type_header === "banner" ? TAB_BANNER : TAB_FULL;

  return (
    <div className="u-col u-gap16">
      <TitleBlock
        title="マイサイトのデザイン"
        description="キービジュアルの配置方法を選択し、画像・動画をアップロードします。見出しの表示名・タイトルはプロフィールの設定を共通利用します。"
      />

      <div className="u-col u-w100 u-gap8">
        <p className="bodytext-13">キービジュアルの配置方法を選択できます。</p>
        {/* Tab と c-tab1 を同一の親 div に隣接配置することで、上下を視覚的に
            連結する（親に gap を持たせない）。 */}
        <div className="u-col u-w100">
          <Tab
            type="tab"
            list={[TAB_FULL, TAB_BANNER]}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          <div className="u-col u-w100 u-gap16 c-tab1">
            {activeTab === TAB_FULL ? (
              <>
                <KeyVisualUploader
                  label="フルスクリーン用キービジュアル（PC）"
                  url={header.key_visual_pc_url}
                  ratio={16 / 10}
                  onUploaded={(fileKey) => save({ key_visual_pc_key: fileKey })}
                  onCleared={() => save({ key_visual_pc_key: "" })}
                />
                <KeyVisualUploader
                  label="モバイル用キービジュアル（SP）"
                  url={header.key_visual_sp_url}
                  ratio={10 / 16}
                  onUploaded={(fileKey) => save({ key_visual_sp_key: fileKey })}
                  onCleared={() => save({ key_visual_sp_key: "" })}
                />
              </>
            ) : (
              <KeyVisualUploader
                label="バナー（2.35:1）"
                url={header.banner_url}
                ratio={2.35}
                onUploaded={(fileKey) => save({ banner_key: fileKey })}
                onCleared={() => save({ banner_key: "" })}
              />
            )}
            <span className="bodytext-11 u-text-light-gray">
              JPG / PNG（画像）または MP4 / MOV（動画）をアップロードできます。アップロード後、自動で最適化されたものが公開ページに反映されます。
            </span>
          </div>
        </div>
      </div>

      <div className="u-col u-w100 u-gap8">
        <p className="bodytext-13">作品の展示方法を選択できます。</p>
        <div className="u-col u-w100">
          <Tab
            type="tab"
            list={[LAYOUT_TAB_THUMBNAIL, LAYOUT_TAB_CIRCLE, LAYOUT_TAB_LIST]}
            activeTab={LAYOUT_TO_TAB[workLayout]}
            onTabChange={handleLayoutTabChange}
          />
          <div className="u-col u-w100 u-gap16 c-tab1">
            <span className="bodytext-11 u-text-light-gray">
              {LAYOUT_DESCRIPTIONS[workLayout]}
            </span>
          </div>
        </div>
      </div>

      <div className="u-row-end u-w100">
        <StatusIndicator status={status} inline />
      </div>
    </div>
  );
};

export default MySiteDesign;
